import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Edit, Eye, Printer, X, Search,
  ShoppingCart, Package, Check, FileText, ScanLine
} from 'lucide-react';
import BarcodeScanner from '../../components/BarcodeScanner';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import ProjectSelect from '../../components/ProjectSelect';

const UNITS = ['pcs', 'sqft', 'rft', 'kg', 'ltr', 'mtr', 'sheet', 'bag', 'box', 'ton', 'set', 'pair'];
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  ordered: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  received: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};
const STATUS_LABELS = { draft: 'Draft', ordered: 'Ordered', partial: 'Partial', received: 'Received', cancelled: 'Cancelled' };

const emptyItem = { item_name: '', description: '', quantity: '', unit: 'pcs', rate: '' };
const emptyForm = {
  supplier_name: '', supplier_phone: '', supplier_address: '', supplier_gstin: '',
  project_id: '', order_date: new Date().toISOString().slice(0, 10),
  expected_delivery: '', status: 'draft', notes: '', tax_percent: '18', discount_amount: '0',
  manual_number: '',
  items: [{ ...emptyItem }],
};

export default function PurchaseOrderPage() {
  const { formatCurrency, formatDate, settings } = useSettings();
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [poMode, setPoMode] = useState('auto');
  const [lastUsedPO, setLastUsedPO] = useState(null);
  const [projectItems, setProjectItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const printRef = useRef();

  // Barcode scanner
  const { scannerOpen, openScanner, closeScanner, handleScan, scanning } = useBarcodeScanner({
    onItemFound: (scannedItem) => {
      setForm(f => {
        // If only one empty row, replace it; otherwise append
        const hasEmptyOnly = f.items.length === 1 && !f.items[0].item_name.trim();
        const newItems = hasEmptyOnly ? [scannedItem] : [...f.items, scannedItem];
        return { ...f, items: newItems };
      });
    },
  });

  const loadNumberingInfo = () => {
    api.get('/numbering-info').then(r => {
      setPoMode(r.data?.po?.mode || 'auto');
      setLastUsedPO(r.data?.po?.last_used || null);
    }).catch(() => {});
  };

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, pRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/projects'),
      ]);
      setOrders(oRes.data);
      setProjects(pRes.data.filter(p => p.status !== 'deleted'));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadNumberingInfo(); }, []);

  // ── Calculations ────────────────────────────────────────────────────────
  const calcTotals = (items, taxPct, disc) => {
    const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.rate) || 0), 0);
    const discAmt = parseFloat(disc) || 0;
    const taxable = subtotal - discAmt;
    const taxAmt = (taxable * (parseFloat(taxPct) || 0)) / 100;
    return { subtotal, discAmt, taxAmt, total: taxable + taxAmt };
  };

  const totals = calcTotals(form.items, form.tax_percent, form.discount_amount);

  // ── Form helpers ────────────────────────────────────────────────────────
  const setItem = (i, field, val) => {
    const next = [...form.items];
    next[i] = { ...next[i], [field]: val };
    setForm(f => ({ ...f, items: next }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };

  const handleProjectChange = async (projectId) => {
    setForm(f => ({ ...f, project_id: projectId }));
    setProjectItems([]);
    if (!projectId) return;
    setLoadingItems(true);
    try {
      const res = await api.get(`/projects/${projectId}`);
      const items = res.data.items || [];
      setProjectItems(items);
    } catch { /* silently ignore */ }
    finally { setLoadingItems(false); }
  };

  const applyProjectItems = () => {
    if (!projectItems.length) return;
    const mapped = projectItems.map(pi => ({
      item_name: pi.item_name || '',
      description: pi.description || (pi.dimensions ? pi.dimensions : '') || '',
      quantity: String(pi.quantity || 1),
      unit: pi.unit || 'pcs',
      rate: pi.unit_price ? String(pi.unit_price) : '',
    }));
    setForm(f => ({ ...f, items: mapped }));
  };

  const openCreate = () => {
    setEditId(null);
    setProjectItems([]);
    setForm({ ...emptyForm, order_date: new Date().toISOString().slice(0, 10), items: [{ ...emptyItem }] });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await api.get(`/purchase-orders/${id}`);
      const o = res.data;
      setEditId(id);
      setForm({
        supplier_name: o.supplier_name, supplier_phone: o.supplier_phone || '',
        supplier_address: o.supplier_address || '', supplier_gstin: o.supplier_gstin || '',
        project_id: o.project_id || '', order_date: o.order_date?.slice(0, 10) || '',
        expected_delivery: o.expected_delivery?.slice(0, 10) || '',
        status: o.status, notes: o.notes || '',
        tax_percent: String(o.tax_percent || '18'),
        discount_amount: String(o.discount_amount || '0'),
        items: o.items.length ? o.items.map(i => ({
          item_name: i.item_name, description: i.description || '',
          quantity: String(i.quantity), unit: i.unit, rate: String(i.rate),
        })) : [{ ...emptyItem }],
      });
      setShowModal(true);
    } catch { toast.error('Failed to load data'); }
  };

  const openView = async (id) => {
    try {
      const res = await api.get(`/purchase-orders/${id}`);
      setViewOrder(res.data);
    } catch { toast.error('Failed to load data'); }
  };

  const handleSave = async () => {
    if (!form.supplier_name.trim()) return toast.error('Supplier name required');
    const validItems = form.items.filter(i => i.item_name.trim() && i.quantity);
    if (!validItems.length) return toast.error('Please add at least one item');
    setSaving(true);
    try {
      const payload = { ...form, items: validItems };
      if (editId) {
        await api.put(`/purchase-orders/${editId}`, payload);
        toast.success('Purchase Order updated successfully ✅');
      } else {
        await api.post('/purchase-orders', payload);
        toast.success('Purchase Order created successfully ✅');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/purchase-orders/${deleteTarget.id}`);
      toast.success('Deleted');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Purchase Order</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Arial', sans-serif; font-size: 12px; color: #1a1a1a; background: white; }
        .po-wrap { max-width: 800px; margin: 20px auto; padding: 30px; border: 1px solid #e0e0e0; }
        .po-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #1a56db; }
        .company-name { font-size: 22px; font-weight: 800; color: #1a56db; }
        .company-sub { font-size: 11px; color: #555; margin-top: 3px; }
        .po-title { text-align: right; }
        .po-title h2 { font-size: 20px; font-weight: 700; color: #1a56db; letter-spacing: 1px; }
        .po-title .po-num { font-size: 14px; font-weight: 600; color: #333; margin-top: 2px; }
        .badges { display: flex; gap: 8px; margin-top: 6px; justify-content: flex-end; }
        .badge { padding: 2px 10px; border-radius: 12px; font-size: 10px; font-weight: 600; text-transform: uppercase; }
        .badge-blue { background: #dbeafe; color: #1d4ed8; }
        .badge-gray { background: #f3f4f6; color: #374151; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
        .info-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; }
        .info-box h4 { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #64748b; margin-bottom: 8px; letter-spacing: 0.5px; }
        .info-box p { font-size: 12px; color: #1e293b; margin-bottom: 3px; line-height: 1.5; }
        .info-box .name { font-size: 14px; font-weight: 700; color: #0f172a; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        thead tr { background: #1a56db; color: white; }
        th { padding: 9px 10px; text-align: left; font-size: 11px; font-weight: 600; }
        th:last-child, td:last-child { text-align: right; }
        td { padding: 8px 10px; font-size: 12px; border-bottom: 1px solid #f1f5f9; }
        tr:nth-child(even) td { background: #f8fafc; }
        .totals { display: flex; justify-content: flex-end; margin-bottom: 20px; }
        .totals-box { min-width: 260px; }
        .total-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 12px; border-bottom: 1px solid #f1f5f9; }
        .total-row.grand { font-size: 14px; font-weight: 700; color: #1a56db; border-top: 2px solid #1a56db; border-bottom: none; padding-top: 8px; margin-top: 4px; }
        .notes-box { background: #fffbeb; border: 1px solid #fbbf24; border-radius: 6px; padding: 10px 14px; margin-bottom: 20px; font-size: 11px; color: #78350f; }
        .footer { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 30px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
        .sign-box { text-align: center; }
        .sign-line { border-top: 1px solid #94a3b8; margin-top: 40px; padding-top: 6px; font-size: 10px; color: #64748b; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } .po-wrap { border: none; margin: 0; padding: 20px; } }
      </style></head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // ── Filtered list ────────────────────────────────────────────────────────
  const filtered = orders.filter(o => {
    if (filterStatus && o.status !== filterStatus) return false;
    if (search && !o.po_number.toLowerCase().includes(search.toLowerCase()) &&
      !o.supplier_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const companyName = settings.company_name || 'MOZI INNOVATORS LLP';
  const companyAddress = settings.company_address || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-blue-600" /> Purchase Orders
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage purchase orders for materials and supplies</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> New Purchase Order
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by PO number or supplier..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ShoppingCart className="w-12 h-12 mb-3 opacity-30" />
            <p className="font-medium">No Purchase Orders found</p>
            <p className="text-sm mt-1">Click the New Purchase Order button to create one</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">PO Number</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600 text-sm">{o.po_number}</span>
                      <div className="text-xs text-gray-400 mt-0.5">{o.item_count} items</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-sm">{o.supplier_name}</div>
                      {o.supplier_phone && <div className="text-xs text-gray-400">{o.supplier_phone}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-600">{o.project_name || '—'}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-gray-600">{formatDate(o.order_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[o.status]}`}>
                        {STATUS_LABELS[o.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 text-sm">{formatCurrency(o.total_amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openView(o.id)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => openEdit(o.id)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteTarget(o)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── CREATE / EDIT MODAL ── */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:860,marginTop:16,marginBottom:16}}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                {editId ? 'Edit Purchase Order' : 'Create New Purchase Order'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[80vh]">
              {/* Supplier Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-blue-500" /> Supplier Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Supplier Name <span className="text-red-500">*</span></label>
                    <input value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                      placeholder="e.g. Ram Timber Store"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                    <input value={form.supplier_phone} onChange={e => setForm(f => ({ ...f, supplier_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">GSTIN</label>
                    <input value={form.supplier_gstin} onChange={e => setForm(f => ({ ...f, supplier_gstin: e.target.value }))}
                      placeholder="27AABCU9603R1ZX"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                    <input value={form.supplier_address} onChange={e => setForm(f => ({ ...f, supplier_address: e.target.value }))}
                      placeholder="Supplier ka address"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                </div>
              </div>

              {/* Order Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" /> Order Info</h3>
                {/* Manual number input — shown only in manual mode and only for new PO */}
                {poMode === 'manual' && !editId && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <label className="block text-xs font-semibold text-amber-800 mb-1">PO Number (Manual) <span className="text-red-500">*</span></label>
                    <input value={form.manual_number} onChange={e => setForm(f => ({ ...f, manual_number: e.target.value }))}
                      placeholder={lastUsedPO ? `Last used: ${lastUsedPO}` : 'Enter PO number e.g. PO-2025'}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
                    {lastUsedPO && <p className="text-xs text-amber-600 mt-1">Last used: <b>{lastUsedPO}</b></p>}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Project (Optional)</label>
                    <ProjectSelect projects={projects} value={form.project_id} onChange={v => handleProjectChange(v)}
              placeholder="— No Project —" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    {loadingItems && <p className="text-xs text-blue-500 mt-1">Loading project items...</p>}
                    {!loadingItems && projectItems.length > 0 && (
                      <button type="button" onClick={applyProjectItems}
                        className="mt-1.5 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg py-1.5 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Import {projectItems.length} items from project
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Order Date <span className="text-red-500">*</span></label>
                    <input type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Expected Delivery</label>
                    <input type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                      {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Items / Materials</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={openScanner}
                      className="flex items-center gap-1 text-amber-600 hover:bg-amber-50 px-2 py-1 rounded-lg text-xs font-medium transition-colors border border-amber-200">
                      <ScanLine className="w-3.5 h-3.5" /> {scanning ? 'Looking up...' : 'Scan'}
                    </button>
                    <button onClick={addItem} className="flex items-center gap-1 text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-lg text-xs font-medium transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add Item
                    </button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">#</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Item Name</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 hidden sm:table-cell">Description</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Unit</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Rate</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                        <th className="px-2 py-2 text-xs text-gray-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, i) => {
                        const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
                        return (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-xs text-gray-400">{i + 1}</td>
                            <td className="px-2 py-2">
                              <input value={item.item_name} onChange={e => setItem(i, 'item_name', e.target.value)}
                                placeholder="Material name" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </td>
                            <td className="px-2 py-2 hidden sm:table-cell">
                              <input value={item.description} onChange={e => setItem(i, 'description', e.target.value)}
                                placeholder="Optional" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </td>
                            <td className="px-2 py-2 w-16">
                              <input type="number" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)}
                                placeholder="0" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </td>
                            <td className="px-2 py-2 w-20">
                              <select value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                                {UNITS.map(u => <option key={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-2 w-20">
                              <input type="number" value={item.rate} onChange={e => setItem(i, 'rate', e.target.value)}
                                placeholder="0" className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-semibold text-gray-700">
                              {formatCurrency(lineTotal)}
                            </td>
                            <td className="px-2 py-2">
                              {form.items.length > 1 && (
                                <button onClick={() => removeItem(i)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-full max-w-xs space-y-2 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span><span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 flex-1">Discount</span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400 text-xs">₹</span>
                      <input type="number" value={form.discount_amount} onChange={e => setForm(f => ({ ...f, discount_amount: e.target.value }))}
                        className="w-20 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 flex-1">GST/Tax %</span>
                    <div className="flex items-center gap-1">
                      <input type="number" value={form.tax_percent} onChange={e => setForm(f => ({ ...f, tax_percent: e.target.value }))}
                        className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400" />
                      <span className="text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tax Amount</span><span>{formatCurrency(totals.taxAmt)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-blue-700 border-t border-gray-200 pt-2">
                    <span>Total</span><span>{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Terms</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Delivery instructions, payment terms, etc."
                  rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
              </div>
            </div>

            {/* Barcode Scanner */}
            {scannerOpen && (
              <BarcodeScanner
                title="Scan Item Barcode"
                onScan={handleScan}
                onClose={closeScanner}
              />
            )}

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
                {saving ? 'Saving...' : <><Check className="w-4 h-4" /> {editId ? 'Update' : 'Create PO'}</>}
              </button>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* ── VIEW / PRINT MODAL ── */}
      {viewOrder && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:720,marginTop:16,marginBottom:16}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">{viewOrder.po_number}</h2>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                  <Printer className="w-4 h-4" /> Print / Download
                </button>
                <button onClick={() => setViewOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* Printable Content */}
            <div ref={printRef} className="p-0">
              <div className="po-wrap" style={{ maxWidth: '720px', margin: '0 auto', padding: '28px', fontFamily: 'Arial, sans-serif' }}>
                {/* PO Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '14px', borderBottom: '2px solid #1a56db' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#1a56db' }}>{companyName}</div>
                    {companyAddress && <div style={{ fontSize: '11px', color: '#555', marginTop: '3px' }}>{companyAddress}</div>}
                    {settings.company_gstin && <div style={{ fontSize: '11px', color: '#555' }}>GSTIN: {settings.company_gstin}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1a56db', letterSpacing: '1px' }}>PURCHASE ORDER</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginTop: '2px' }}>{viewOrder.po_number}</div>
                    <div style={{ marginTop: '6px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8', textTransform: 'uppercase' }}>
                        {STATUS_LABELS[viewOrder.status]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Info Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' }}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '6px', letterSpacing: '0.5px' }}>Supplier / Vendor</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{viewOrder.supplier_name}</div>
                    {viewOrder.supplier_phone && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>📞 {viewOrder.supplier_phone}</div>}
                    {viewOrder.supplier_gstin && <div style={{ fontSize: '11px', color: '#475569' }}>GSTIN: {viewOrder.supplier_gstin}</div>}
                    {viewOrder.supplier_address && <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{viewOrder.supplier_address}</div>}
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '6px', letterSpacing: '0.5px' }}>Order Details</div>
                    <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '3px' }}><b>Order Date:</b> {formatDate(viewOrder.order_date)}</div>
                    {viewOrder.expected_delivery && <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '3px' }}><b>Delivery By:</b> {formatDate(viewOrder.expected_delivery)}</div>}
                    {viewOrder.project_name && <div style={{ fontSize: '12px', color: '#1e293b' }}><b>Project:</b> {viewOrder.project_name}</div>}
                    <div style={{ fontSize: '12px', color: '#1e293b', marginTop: '2px' }}><b>Created By:</b> {viewOrder.created_by_name}</div>
                  </div>
                </div>

                {/* Items Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
                  <thead>
                    <tr style={{ background: '#1a56db', color: 'white' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>#</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>Item Name</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: 600 }}>Description</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 600 }}>Qty</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 600 }}>Unit</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: 600 }}>Rate</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', fontWeight: 600 }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewOrder.items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ padding: '7px 10px', fontSize: '12px', color: '#64748b' }}>{i + 1}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{it.item_name}</td>
                        <td style={{ padding: '7px 10px', fontSize: '11px', color: '#64748b' }}>{it.description || '—'}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'center' }}>{it.quantity}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'center', color: '#64748b' }}>{it.unit}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'right' }}>{formatCurrency(it.rate)}</td>
                        <td style={{ padding: '7px 10px', fontSize: '12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px' }}>
                  <div style={{ minWidth: '240px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#374151' }}>
                      <span>Subtotal</span><span>{formatCurrency(viewOrder.subtotal)}</span>
                    </div>
                    {parseFloat(viewOrder.discount_amount) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#374151' }}>
                        <span>Discount</span><span>- {formatCurrency(viewOrder.discount_amount)}</span>
                      </div>
                    )}
                    {parseFloat(viewOrder.tax_percent) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#374151' }}>
                        <span>GST @ {viewOrder.tax_percent}%</span><span>{formatCurrency(viewOrder.tax_amount)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', fontSize: '15px', fontWeight: 700, color: '#1a56db', borderTop: '2px solid #1a56db', marginTop: '4px' }}>
                      <span>TOTAL</span><span>{formatCurrency(viewOrder.total_amount)}</span>
                    </div>
                  </div>
                </div>

                {viewOrder.notes && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '10px 14px', marginBottom: '18px', fontSize: '11px', color: '#78350f' }}>
                    <b>Notes:</b> {viewOrder.notes}
                  </div>
                )}

                {/* Footer */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '30px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '1px solid #94a3b8', marginTop: '40px', paddingTop: '6px', fontSize: '10px', color: '#64748b' }}>
                      Authorized Signatory
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderTop: '1px solid #94a3b8', marginTop: '40px', paddingTop: '6px', fontSize: '10px', color: '#64748b' }}>
                      Supplier Acknowledgement
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Purchase Order?</h3>
            <p className="text-sm text-gray-600 mb-5"><b>{deleteTarget.po_number}</b> will be permanently deleted. This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium">Delete</button>
            </div>
          </div>
            </div>
        </div>
      )}
    </div>
  );
}
